import type { AppState } from '../state/appState.js';
import type { CommandBatch } from '../editor/commandHistory.js';
import { adjustParamLabel } from '../editor/historyLabels.js';
import { readParamValue, scopedParamKey, type ParamGraphSlot } from '../graph/paramKeys.js';
import { getValueNodeBounds, getValueNodeComponentCount, getValueNodeOutputType, getValueNodeSubtype, getValueSubtypeLabel, isValueNode, type GraphNode, type OutputValue, type ParamValue, type ValuePortType } from '../graph/nodeDefs.js';
import { hexToRgb, rgbToHex } from '../math/color.js';
import { createDragger, type DragFieldElement } from './controls/dragField.js';
import { createColorControl, createEditableColorField } from './colorField.js';

export interface ParamRendererDeps {
  appState: AppState;
  commandBatch: CommandBatch;
  /**
   * Where an edit lands, and where the field reads back from. These are a pair
   * on purpose: a caller that writes a scene node's override must read that
   * override too, or the field shows the layer's value while editing the node's
   * and the two drift apart on screen.
   */
  setParamValue: (key: string, value: OutputValue) => void;
  readParam?: (key: string) => ParamValue | undefined;
  scheduleRender: () => void;
  refreshIntrinsicSizeDisplay: () => void;
}

export interface RenderExposedParamsOptions {
  container: HTMLElement;
  nodes: GraphNode[];
  scopeId: string | null;
  slot: ParamGraphSlot;
}

function readExposedValue(type: ValuePortType, stored: ParamValue | undefined): number[] {
  const count = getValueNodeComponentCount(type);
  if (typeof stored === 'number') {
    return Array.from({ length: count }, (_, i) => i === 0 ? stored : 0);
  }
  if (Array.isArray(stored)) {
    return Array.from({ length: count }, (_, i) => i < stored.length ? Number(stored[i]) : 0);
  }
  return Array.from({ length: count }, () => 0);
}

function defaultValueForNode(node: GraphNode): OutputValue {
  const d = node.data as Record<string, unknown>;
  const type = getValueNodeOutputType(node);
  if (type === 'float') return (d.value0 as number) ?? 0;
  const count = getValueNodeComponentCount(type);
  return [d.value0 as number ?? 0, d.value1 as number ?? 0, d.value2 as number ?? 0, d.value3 as number ?? 1].slice(0, count);
}

function writeComponent(base: number[], index: number, nextValue: number): number[] {
  const next = [...base];
  next[index] = nextValue;
  return next;
}

function refreshAfterParamChange(deps: ParamRendererDeps): void {
  deps.scheduleRender();
  deps.refreshIntrinsicSizeDisplay();
}

function createParamRow(labelText: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'param-row';

  const label = document.createElement('span');
  label.className = 'param-name';
  label.textContent = labelText;
  label.title = labelText;
  row.appendChild(label);

  return row;
}

function createColorPickerField(
  value: string,
  beginHistory: () => void,
  onInputValue: (hex: string) => void,
  commitHistory: () => void,
  opacity?: number,
  onOpacityInput?: (opacity: number) => void,
): ReturnType<typeof createEditableColorField> {
  return createEditableColorField({
    value,
    opacity,
    classNames: {
      field: 'param-color-field',
      swatchButton: 'param-color-swatch-button',
      swatch: 'param-color-swatch',
      valueInput: 'param-color-value',
    },
    inputAriaLabel: 'Hex color',
    onBegin: beginHistory,
    onInputValue,
    onOpacityInput,
    onCommit: commitHistory,
  });
}

export function getExposedValueNodes(nodes: GraphNode[]): GraphNode[] {
  return nodes.filter(isValueNode).filter(node => (node.data as Record<string, unknown>).exposed);
}

export function renderExposedParams(deps: ParamRendererDeps, options: RenderExposedParamsOptions): number {
  const appState = deps.appState;
  const valueNodes = getExposedValueNodes(options.nodes);
  const list = options.container;
  list.innerHTML = '';

  valueNodes.forEach(node => {
    const d = node.data as Record<string, unknown>;
    const outputType = getValueNodeOutputType(node);
    const subtype = getValueNodeSubtype(node);
    const scopeId = options.scopeId;
    const key = scopedParamKey(scopeId, node.id, options.slot);
    const fallback = defaultValueForNode(node);

    const readParam = deps.readParam
      ?? ((k: string): ParamValue | undefined => appState.params[k]);
    // Seeding is for the global store only: an override that does not exist is
    // the node saying "use the layer's value", and writing one on open would
    // turn opening the panel into an edit.
    if (!deps.readParam && appState.params[key] === undefined) {
      appState.params[key] = readParamValue(scopeId, appState.params, node.id, fallback, options.slot);
    }
    const currentParam = (): ParamValue | undefined =>
      readParam(key) ?? readParamValue(scopeId, appState.params, node.id, fallback, options.slot);

    const row = createParamRow((d.name as string) || getValueSubtypeLabel(subtype));

    const historyLabel = adjustParamLabel((d.name as string) || getValueSubtypeLabel(subtype));
    let historyStarted = false;
    const beginHistory = (): void => {
      if (historyStarted) return;
      deps.commandBatch.begin(historyLabel);
      historyStarted = true;
    };
    const commitHistory = (): void => {
      if (!historyStarted) return;
      historyStarted = false;
      deps.commandBatch.commit();
    };
    const dragHistory = {
      onBegin: beginHistory,
      onEnd: (changed: boolean): void => {
        if (changed) commitHistory();
      },
    };

    if (outputType === 'float') {
      const stored = currentParam();
      const currentValue = typeof stored === 'number' ? stored : (d.value0 as number) ?? 0;
      const bounds = getValueNodeBounds(d as any);

      const valueInput = createDragger(
        currentValue,
        bounds.min,
        bounds.max,
        0.01,
        nextValue => {
          beginHistory();
          deps.setParamValue(key, nextValue);
          refreshAfterParamChange(deps);
        },
        dragHistory,
        { showProgress: bounds.hasSlider, subtype: subtype === 'default' ? undefined : subtype },
      );
      valueInput.style.flex = '1';
      row.appendChild(valueInput);

      list.appendChild(row);
      return;
    }
    const values = readExposedValue(outputType, currentParam());
    const freshValues = () => readExposedValue(outputType, currentParam());
    if (node.type === 'Color' || (subtype === 'color' && (outputType === 'vec3' || outputType === 'vec4'))) {
      let alphaInput: DragFieldElement | undefined;
      const colorField = createColorPickerField(
        rgbToHex(values[0] ?? 0, values[1] ?? 0, values[2] ?? 0),
        beginHistory,
        (nextHex) => {
          const [red, green, blue] = hexToRgb(nextHex);
          const cur = freshValues();
          cur[0] = red;
          cur[1] = green;
          cur[2] = blue;
          deps.setParamValue(key, cur);
          refreshAfterParamChange(deps);
        },
        commitHistory,
        outputType === 'vec4' ? values[3] ?? 1 : undefined,
        outputType === 'vec4' ? value => {
          beginHistory();
          deps.setParamValue(key, writeComponent(freshValues(), 3, value));
          alphaInput?.setValue(value);
          refreshAfterParamChange(deps);
        } : undefined,
      );
      let alphaControl: HTMLElement | undefined;

      if (outputType === 'vec4') {
        const alpha = createDragger(
          values[3] ?? 1,
          0,
          1,
          0.01,
          value => {
            beginHistory();
            deps.setParamValue(key, writeComponent(freshValues(), 3, value));
            colorField.setOpacity(value);
            refreshAfterParamChange(deps);
          },
          dragHistory,
          { subtype: 'percentage' },
        );
        alpha.style.width = '80px';
        alphaInput = alpha;
        alphaControl = alpha;
      }

      row.appendChild(createColorControl({
        classNames: {
          container: 'param-color-controls',
          leading: 'param-inline-control param-inline-control--leading',
          trailing: 'param-inline-control param-inline-control--trailing',
        },
        colorField,
        trailingControl: alphaControl,
      }));
      list.appendChild(row);
      return;
    }

    values.forEach((value, index) => {
      const dragger = createDragger(
        value,
        -1000,
        1000,
        0.01,
        nextValue => {
          beginHistory();
          deps.setParamValue(key, writeComponent(freshValues(), index, nextValue));
          refreshAfterParamChange(deps);
        },
        dragHistory,
      );
      dragger.style.width = '52px';
      row.appendChild(dragger);
    });
    list.appendChild(row);
  });

  return valueNodes.length;
}
